import React, { useEffect, useRef, useState } from "react";
import { Upload, FileText, Trash2, RefreshCw, Info } from "lucide-react";
import { Button, StatusBadge, toast, LoadingBlock, EmptyState, ConfirmDialog } from "@/components/ds";
import { api, formatApiError, type KnowledgeDocument } from "@/lib/api";
import { DOC_STATUS_META, fmtSize, fmtDate } from "./constants";

export function Documents() {
  const [docs, setDocs] = useState<KnowledgeDocument[] | null>(null);
  const [uploading, setUploading] = useState(false);
  const [toDelete, setToDelete] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = () => api.kbDocuments().then(setDocs).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["pdf", "docx", "txt"].includes(ext ?? "")) { toast.error("Only PDF, DOCX and TXT files are supported."); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("File exceeds the 15 MB limit."); return; }
    setUploading(true);
    try {
      await api.kbUpload(file);
      toast.success("File uploaded — stored, not yet processed");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id: string) => {
    try { await api.kbDeleteDocument(id); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const retry = async (id: string) => {
    try { const r = await api.kbRetryDocument(id); toast.info((r.note as string) ?? "Processing not available yet"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="kb-documents">
      <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3" data-testid="kb-doc-notice">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
        <p className="text-sm text-blue-800">
          Uploaded documents are <strong>stored securely but not yet read by the AI</strong>. Automatic parsing and
          indexing is coming soon — until then, add key facts as knowledge entries so your AI can use them.
        </p>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">PDF, DOCX or TXT · up to 15 MB</p>
        <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" className="hidden" onChange={onFile} data-testid="kb-file-input" />
        <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="kb-upload-btn">
          <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload document"}
        </Button>
      </div>

      {docs === null ? (
        <LoadingBlock rows={3} />
      ) : docs.length === 0 ? (
        <EmptyState icon={FileText} title="No documents yet" description="Upload a PDF, DOCX or TXT to keep it on file." />
      ) : (
        <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
          {docs.map((d) => {
            const meta = DOC_STATUS_META[d.processing_status] ?? DOC_STATUS_META.uploaded;
            return (
              <div key={d.id} className="flex items-center justify-between px-5 py-4" data-testid={`kb-doc-${d.id}`}>
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                    <FileText className="h-[18px] w-[18px]" />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-900">{d.original_filename}</p>
                    <p className="text-xs text-slate-400">{d.extension.toUpperCase()} · {fmtSize(d.size)} · {fmtDate(d.created_at)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge tone={meta.tone} data-testid={`kb-doc-status-${d.id}`}>{meta.label}</StatusBadge>
                  {!d.ai_ready && (
                    <Button variant="ghost" size="sm" onClick={() => retry(d.id)} data-testid={`kb-doc-retry-${d.id}`} title="Retry processing">
                      <RefreshCw className="h-4 w-4 text-slate-400" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => setToDelete(d.id)} data-testid={`kb-doc-delete-${d.id}`}>
                    <Trash2 className="h-4 w-4 text-slate-400" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}
        title="Delete this document?" description="This removes the file from your knowledge base."
        confirmLabel="Delete" destructive
        onConfirm={() => { if (toDelete) remove(toDelete); setToDelete(null); }}
        testid="kb-doc-delete-confirm"
      />
    </div>
  );
}
